// src/composables/api/ticketing/useTicketingWidgetApi.ts
import { useAuthHttp } from '@/composables/api/useAuthHttp'
import type { AxiosRequestConfig } from 'axios'

const PREFIX = '/public/ticketing'

export function useTicketingWidgetApi() {
  const { get, post, patch, del } = useAuthHttp()

  const fetchEventBySlug = (
    slug: string,
    config: AxiosRequestConfig = {}
  ) =>
    get(`${PREFIX}/events/${encodeURIComponent(slug)}`, {
      ...config,
      ignoreAuthRedirect: true, // Allow widget to load even if auth token is invalid/expired
      params: {
        ...(config.params || {}),
      },
    })

  const createSessionForEvent = (slug: string, config: AxiosRequestConfig = {}) =>
    post(`${PREFIX}/events/${encodeURIComponent(slug)}/sessions`, {}, { ...config, ignoreAuthRedirect: true })

  const fetchSessionById = (sessionId: string, config: AxiosRequestConfig = {}) =>
    get(`${PREFIX}/sessions/${encodeURIComponent(sessionId)}`, { ...config, ignoreAuthRedirect: true })

  const addSessionItem = (
    sessionId: string,
    payload: { ticket_id: number; quantity: number },
    config: AxiosRequestConfig = {}
  ) =>
    post(
      `${PREFIX}/sessions/${encodeURIComponent(sessionId)}/items`,
      payload,
      { ...config, ignoreAuthRedirect: true }
    )

  const updateSessionItemQty = (
    sessionId: string,
    itemId: number,
    quantity: number,
    config: AxiosRequestConfig = {}
  ) =>
    patch(
      `${PREFIX}/sessions/${encodeURIComponent(sessionId)}/items/${itemId}`,
      { quantity },
      { ...config, ignoreAuthRedirect: true }
    )

  const removeSessionItem = (
    sessionId: string,
    itemId: number,
    config: AxiosRequestConfig = {}
  ) =>
    del(
      `${PREFIX}/sessions/${encodeURIComponent(sessionId)}/items/${itemId}`,
      { ...config, ignoreAuthRedirect: true }
    )

  const applySessionDiscount = (
    token: string,
    code: string,
    config: AxiosRequestConfig = {}
  ) =>
    post(
      `${PREFIX}/sessions/${encodeURIComponent(token)}/discounts`,
      { code },
      { ...config, ignoreAuthRedirect: true }
    )

  const removeSessionDiscount = (
    sessionPublicToken: string,
    config: AxiosRequestConfig = {}
  ) =>
    del(
      `${PREFIX}/sessions/${encodeURIComponent(sessionPublicToken)}/discounts`,
      { ...config, ignoreAuthRedirect: true }
    )

  /** Save consents (T&Cs + marketing, optional name/email/ip captured server-side) */
  const saveSessionConsents = (
    sessionPublicToken: string,
    payload: {
      accepted_terms: boolean
      mailing_list?: boolean
      first_name?: string | null
      last_name?: string | null
      email?: string | null
    },
    config: AxiosRequestConfig = {}
  ) =>
    post(
      `${PREFIX}/sessions/${encodeURIComponent(sessionPublicToken)}/consents`,
      payload,
      { ...config, ignoreAuthRedirect: true }
    )

  /** Create/reuse PaymentIntent for this session */
  const createPaymentIntentForSession = (
    sessionPublicToken: string,
    config: AxiosRequestConfig = {}
  ) =>
    post(
      `${PREFIX}/sessions/${encodeURIComponent(sessionPublicToken)}/payments/intent`,
      {},
      { ...config, ignoreAuthRedirect: true }
    )
  // response.data -> { client_secret, payment_intent_id }

  /** Optional server-side confirm/finalize safety */
  const confirmSessionPayment = (
    sessionPublicToken: string,
    config: AxiosRequestConfig = {}
  ) =>
    post(
      `${PREFIX}/sessions/${encodeURIComponent(sessionPublicToken)}/confirm`,
      {},
      { ...config, ignoreAuthRedirect: true }
    )

  /** NEW: validate cart & place 20-minute holds before checkout */
  const validateSessionBeforeCheckout = (
    sessionPublicToken: string,
    config: AxiosRequestConfig = {}
  ) =>
    post(
      `${PREFIX}/sessions/${encodeURIComponent(sessionPublicToken)}/validate-and-hold`,
      {},
      { ...config, ignoreAuthRedirect: true }
    )

  /** NEW: release any holds for this session (back button, timeout, etc) */
  const releaseSessionHolds = (
    sessionPublicToken: string,
    config: AxiosRequestConfig = {}
  ) =>
    post(
      `${PREFIX}/sessions/${encodeURIComponent(sessionPublicToken)}/release-holds`,
      {},
      { ...config, ignoreAuthRedirect: true }
    )

  /** Extend holds by 5 minutes (used when user starts payment with low time remaining) */
  const extendSessionHold = (
    sessionPublicToken: string,
    config: AxiosRequestConfig = {}
  ) =>
    post(
      `${PREFIX}/sessions/${encodeURIComponent(sessionPublicToken)}/extend-hold`,
      {},
      { ...config, ignoreAuthRedirect: true }
    )

  /** Sync local cart items to the session (used before checkout when no voucher) */
  const syncSessionItems = (
    sessionPublicToken: string,
    items: { ticket_id: number; quantity: number }[],
    config: AxiosRequestConfig = {}
  ) =>
    post(
      `${PREFIX}/sessions/${encodeURIComponent(sessionPublicToken)}/items/sync`,
      { items },
      { ...config, ignoreAuthRedirect: true }
    )

  return {
    fetchEventBySlug,
    createSessionForEvent,
    fetchSessionById,
    addSessionItem,
    updateSessionItemQty,
    removeSessionItem,
    applySessionDiscount,
    removeSessionDiscount,
    saveSessionConsents,
    createPaymentIntentForSession,
    confirmSessionPayment,
    validateSessionBeforeCheckout,
    releaseSessionHolds,
    extendSessionHold,
    syncSessionItems,
  }
}